/*
 * File: FeedbackDialog.tsx
 * Author: Austin Robinson
 * Date Created: 4/11/2025
 * Copyright (c) DrivingDoc 2025
 */
import { useEffect, useRef } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import { DialogProps } from '@toolpad/core/useDialogs';
import { SerializedEditorState } from 'lexical';
import { FormProvider, useForm } from 'react-hook-form';

import { GetTemplateSetParams, useGetTemplateSet } from 'api/hooks/private/templates/GetTemplateSet';

import { RichTextEditor } from 'components/Editor';
import { RichTextEditorProvider } from 'components/Editor/RichTextEditor/RichTextEditorProvider';
import { EmailContainer } from 'components/Editor/ContentContainers/EmailContainer';
import TextInput from 'components/Forms/TextInput';
import { EmailTemplateInputValues, EmailTemplateOutputValues, EmailTemplateSchema } from 'components/TemplateForms';

interface SendEmailDialogPayload {
  /**
   * A title for the dialog. Defaults to `'Send Email'`.
   */
  title?: React.ReactNode;

  /**
   * The text to show in the "Ok" button. Defaults to `'Send'`.
   */
  okText?: React.ReactNode;

  /**
   * The key identifying the template set to fetch.
   */
  templateKey: GetTemplateSetParams['templateKey'];

  /**
   * The unique ID of the template to be edited.
   * If undefined or empty, the form will not fetch any template data.
   */
  id: GetTemplateSetParams['id'];

  /**
   * Callback function called when the form is submitted successfully.
   * Receives the validated email template output values.
   * Should return a Promise that resolves when submission is complete.
   *
   * @param {EmailTemplateOutputValues} data - The validated form data.
   * @returns {Promise<void>}
   */
  onSubmit: (data: EmailTemplateOutputValues) => Promise<void>;
}

export function SendEmailDialog({ payload, open, onClose }: DialogProps<SendEmailDialogPayload>) {
  const hasFormInitialized = useRef(false);

  // fetch template set
  const templateSet = useGetTemplateSet({
    variables: {
      templateKey: payload.templateKey,
      id: payload.id ?? '',
    },
    enabled: !!payload.id,
  });

  // get template merge tags
  const mergeTags = templateSet.data?.mergeTags ?? {
    tree: [],
    map: {},
  };

  const form = useForm<EmailTemplateInputValues, unknown, EmailTemplateOutputValues>({
    mode: 'all',
    resolver: zodResolver(EmailTemplateSchema),
  });

  // initialize the form with the merge tag options and existing template
  useEffect(() => {
    // get existing email template to initialize the form with
    const existingEmailTemplate = templateSet.data?.templateSet?.email;

    if (!hasFormInitialized.current && existingEmailTemplate?.body) {
      // initialize form fields with existing values
      form.reset({
        ...existingEmailTemplate,
        merge_tags: mergeTags.map,
      });

      //   // parse the serialized state
      //   const editorState = editor.parseEditorState(existingEmailTemplate.body as SerializedEditorState);

      //   // update the editor state with existing template
      //   editor.update(() => {
      //     editor.setEditorState(editorState);
      //   });

      // mark form as initialized
      hasFormInitialized.current = true;
    }
  }, [templateSet.data, form.reset]);

  const onSubmit = async (data: EmailTemplateOutputValues) => {
    await payload.onSubmit(data);
    onClose();
  };

  return (
    <Dialog maxWidth="lg" open={open} onClose={() => onClose()}>
      <FormProvider {...form}>
        <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
          <DialogTitle>{payload.title ?? 'Send Email'}</DialogTitle>
          <DialogContent>
            <RichTextEditorProvider>
              <Stack gap={2}>
                <TextInput name="options.bcc.0" label="BCC" disabled={form.formState.isSubmitting} />
                <TextInput
                  name="options.reply_to"
                  label="Reply To Address"
                  helperText="This is where replies are sent"
                  disabled={form.formState.isSubmitting}
                />
                <TextInput name="subject" label="Email Subject" disabled={form.formState.isSubmitting} />
                <Box>
                  <RichTextEditor
                    initialState={templateSet.data?.templateSet?.email?.body as SerializedEditorState}
                    mergeTags={mergeTags}
                    contentEditableWrapper={EmailContainer}
                    onChange={(state) => form.setValue('body', state, { shouldDirty: true, shouldValidate: true })}
                  />
                </Box>
              </Stack>
            </RichTextEditorProvider>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => onClose()}>Cancel</Button>
            <Button disabled={!open} type="submit">
              {payload.okText ?? 'Send'}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={form.formState.isSubmitting}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
}

export default SendEmailDialog;
